import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChildren, QueryList, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DEPARTMENT_COLORS } from 'src/app/request-v2/constants/request-colors.constants';
import { CreateRequestDepartmentDto, RequestDepartment, RequestDepartmentEdition, UpdateRequestDepartmentDto } from '../../../../models/request-department.model';
import { RequestDepartmentService } from '../../../../services/request-department.service';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';

interface AdminUser {
    id: string;
    name: string;
    email: string;
    initials: string;
    picture?: string;
}

@Component({
    selector: 'app-department-form',
    templateUrl: './department-form.component.html',
    styleUrls: ['./department-form.component.scss'],
})
export class DepartmentFormComponent implements OnInit {
    @Input() departmentToEditId: string | null = null;

    @Output() cancel = new EventEmitter<void>();

    @Output() saved = new EventEmitter<RequestDepartment>();

    currentStep = 1;

    totalSteps = 2;

    departmentForm: FormGroup;

    departmentColors = DEPARTMENT_COLORS;

    isDropdownVisible = false;

    isAssigneeDropdownVisible = false;

    selectedAssignee: AdminUser | null = null;

    selectedAssigneeIds: string[] = [];

    // Store manager objects for reference
    managerObjects: { [id: string]: AdminUser } = {};

    // Preloaded admin/manager data for the assignee selector
    preloadedManagerObjects: AdminUser[] = [];

    isSubmitting = false;

    private destroy$ = new Subject<void>();

    // Track word counts
    wordCounts = {
        en: 0,
        fr: 0,
    };

    // Add ViewChildren queries to get direct references to input elements
    @ViewChildren('nameEn') nameEnInputs: QueryList<ElementRef>;

    @ViewChildren('nameFr') nameFrInputs: QueryList<ElementRef>;

    @ViewChild('nameEnInput') nameEnInputRef: ElementRef;

    @ViewChild('nameFrInput') nameFrInputRef: ElementRef;

    constructor(
        private fb: FormBuilder,
        private elementRef: ElementRef,
        private departmentService: RequestDepartmentService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        this.departmentForm = this.fb.group({
            visual: this.fb.group({
                color: [DEPARTMENT_COLORS[0], Validators.required],
            }),
            name: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            description: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            settings: this.fb.group({
                order: [0, Validators.required],
                isActive: [true],
                managers: [[], Validators.required],
            }),
        });

        if (this.departmentToEditId) {
            this.departmentForm.disable();

            this.departmentService.getRequestDepartmentByIdEdition(this.departmentToEditId).subscribe((department) => {
                // Store the original department data
                const departmentColor = department.color || '#4FC3F7';

                // Patch form values
                this.departmentForm.patchValue({
                    visual: {
                        color: departmentColor,
                    },
                    name: department.name,
                    description: department.description,
                    settings: {
                        order: department.order,
                        isActive: department.isActive,
                    },
                });

                // Initialize managers from department
                this.initializeManagersFromDepartment(department);
                this.departmentForm.get('settings.managers')?.setValue(this.selectedAssigneeIds);

                this.departmentForm.enable();
                this.cdr.detectChanges();
            });
        }
    }

    // Helper function to extract manager ID safely
    private getManagerId(manager: any): string {
        if (typeof manager === 'string') {
            return manager;
        }
        if (manager && manager._id) {
            return manager._id;
        }
        return '';
    }

    // Helper function to convert department managers to IDs for internal use
    private convertManagersToIds(managers: any[]): string[] {
        if (!Array.isArray(managers)) {
            return [];
        }

        return managers.map((manager) => this.getManagerId(manager)).filter((id) => id !== ''); // Filter out empty IDs
    }

    // Initialize managers from department
    private initializeManagersFromDepartment(department: RequestDepartmentEdition): void {
        if (!department) return;

        // Ensure managers exists and is an array
        if (!department.managers) {
            department.managers = [];
        }

        // Extract manager IDs and create manager objects
        const { managers } = department;
        this.selectedAssigneeIds = this.convertManagersToIds(managers);

        // Clear previous preloaded data
        this.preloadedManagerObjects = [];

        // Process manager objects to store for reference
        if (Array.isArray(managers)) {
            managers.forEach((manager) => {
                if (typeof manager !== 'string' && manager) {
                    const id = this.getManagerId(manager);
                    if (id && manager.firstName && manager.lastName) {
                        // Create manager object
                        const managerObj = {
                            id,
                            name: `${manager.firstName} ${manager.lastName}`,
                            email: manager.email || '',
                            initials: (manager.firstName.charAt(0) + manager.lastName.charAt(0)).toUpperCase(),
                            picture: manager.picture,
                        };

                        // Store in both collections
                        this.managerObjects[id] = managerObj;
                        this.preloadedManagerObjects.push(managerObj);
                    }
                }
            });
        }

        // If there's at least one manager, set as the selected assignee for display
        if (this.selectedAssigneeIds.length > 0 && this.preloadedManagerObjects.length > 0) {
            [this.selectedAssignee] = this.preloadedManagerObjects;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    updateInitialWordCounts(): void {
        const enDescription = this.departmentForm.get('description.en')?.value || '';
        const frDescription = this.departmentForm.get('description.fr')?.value || '';

        this.wordCounts.en = this.countWords(enDescription);
        this.wordCounts.fr = this.countWords(frDescription);
    }

    updateWordCount(event: Event, field: 'en' | 'fr'): void {
        const textarea = event.target as HTMLTextAreaElement;
        const text = textarea.value;

        this.wordCounts[field] = this.countWords(text);

        // Limit to 80 words
        if (this.wordCounts[field] > 80) {
            const words = text.split(/\s+/);
            const limitedWords = words.slice(0, 80);
            const limitedText = limitedWords.join(' ');

            // Update the form control without triggering another change event
            this.departmentForm.get(`description.${field}`)?.setValue(limitedText, { emitEvent: false });
            this.wordCounts[field] = 80;

            // Set cursor position at end of text
            setTimeout(() => {
                textarea.value = limitedText;
                textarea.selectionStart = limitedText.length;
                textarea.selectionEnd = limitedText.length;
            });
        }
    }

    getWordCount(field: 'en' | 'fr'): number {
        return this.wordCounts[field];
    }

    countWords(text: string): number {
        if (!text || text.trim() === '') {
            return 0;
        }

        // Split by whitespace and filter out empty strings
        return text
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent): void {
        const clickedInside = this.elementRef.nativeElement.contains(event.target);
        if (!clickedInside) {
            if (this.isDropdownVisible) {
                this.isDropdownVisible = false;
            }
            if (this.isAssigneeDropdownVisible) {
                this.isAssigneeDropdownVisible = false;
            }
        }
    }

    toggleDropdownVisibility(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.isDropdownVisible = !this.isDropdownVisible;
    }

    toggleAssigneeDropdownVisibility(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.isAssigneeDropdownVisible = !this.isAssigneeDropdownVisible;
    }

    onColorSelect(color: string): void {
        this.departmentForm.get('visual.color')?.setValue(color);
        // Close the dropdown after selection
        this.isDropdownVisible = false;
    }

    onAssigneeSelect(assignee: AdminUser): void {
        this.selectedAssignee = assignee;

        // Update the form control for backward compatibility
        this.departmentForm.get('settings.assigneeId')?.setValue(assignee.id);

        // Update the selectedAssigneeIds array
        if (!this.selectedAssigneeIds.includes(assignee.id)) {
            this.selectedAssigneeIds = [assignee.id];
        }

        this.isAssigneeDropdownVisible = false;
    }

    onAssigneesSelect(assignees: AdminUser[]): void {
        // Update the selectedAssigneeIds array with all selected assignees
        this.selectedAssigneeIds = assignees.map((a) => a.id);
        // Update the form control value
        this.departmentForm.get('settings.managers')?.setValue(this.selectedAssigneeIds);
        this.departmentForm.get('settings.managers')?.markAsDirty(); // Mark as dirty to trigger validation
        this.departmentForm.get('settings.managers')?.updateValueAndValidity(); // Update validity status

        // If there's at least one assignee, set it as the primary for backward compatibility
        if (assignees.length > 0) {
            const [firstAssignee] = assignees;
            this.selectedAssignee = firstAssignee;
        } else {
            this.selectedAssignee = null;
        }
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onSubmit(): void {
        // Mark all fields as touched to trigger validation messages
        this.departmentForm.markAllAsTouched();

        // Explicitly check if assignees are selected
        if (this.selectedAssigneeIds.length === 0) {
            return; // Stop submission if no assignee is selected
        }

        // Check form validity after marking as touched
        if (this.departmentForm.valid && !this.isSubmitting) {
            this.isSubmitting = true;

            const formValue = this.departmentForm.value;

            // Use the selectedAssigneeIds array for managers
            const managers: string[] = this.selectedAssigneeIds;

            const departmentData: CreateRequestDepartmentDto | UpdateRequestDepartmentDto = {
                name: {
                    en: formValue.name.en,
                    fr: formValue.name.fr,
                },
                description: {
                    en: formValue.description.en,
                    fr: formValue.description.fr,
                },
                color: formValue.visual.color,
                order: formValue.settings.order,
                isActive: formValue.settings.isActive,
                managers, // Use the validated managers list
            };

            // Service will handle converting any manager objects to IDs

            if (this.departmentToEditId) {
                // Update existing department
                this.departmentService
                    .updateRequestDepartment(this.departmentToEditId, departmentData)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (updatedDepartment) => {
                            this.isSubmitting = false;
                            this.showSuccessMessage('request_v2.department_form.update_success');
                            this.saved.emit(updatedDepartment);
                        },
                        error: (_error) => {
                            this.isSubmitting = false;
                            this.showErrorMessage('request_v2.department_form.create_error');
                        },
                    });
            } else {
                // Create new department
                this.departmentService
                    .createRequestDepartment(departmentData as CreateRequestDepartmentDto)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (newDepartment) => {
                            this.isSubmitting = false;
                            this.showSuccessMessage('request_v2.department_form.create_success');
                            this.saved.emit(newDepartment);
                        },
                        error: (_error) => {
                            this.isSubmitting = false;
                            this.showErrorMessage('request_v2.department_form.create_error');
                        },
                    });
            }
        } else {
            // Loop through controls to find which one is invalid
            Object.keys(this.departmentForm.controls).forEach((key) => {
                const controlErrors = this.departmentForm.get(key)?.errors;
                if (controlErrors != null) {
                    console.error('Control:', key, 'Error:', controlErrors);
                }
            });

            // Check nested controls
            Object.keys((this.departmentForm.get('name') as FormGroup).controls).forEach((key) => {
                const controlErrors = this.departmentForm.get(['name', key])?.errors;
                if (controlErrors != null) {
                    console.error('Control: name.', key, 'Error:', controlErrors);
                }
            });
            Object.keys((this.departmentForm.get('settings') as FormGroup).controls).forEach((key) => {
                const controlErrors = this.departmentForm.get(['settings', key])?.errors;
                if (controlErrors != null) {
                    console.error('Control: settings.', key, 'Error:', controlErrors);
                }
            });
        }
    }

    private showSuccessMessage(key: string): void {
        this.messageService.success(this.translate.instant(key));
    }

    private showErrorMessage(key: string): void {
        this.messageService.error(this.translate.instant(key));
    }
}
